import React, { useState } from 'react';
import {
  Box,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import MovieFilterIcon from '@mui/icons-material/MovieFilter';
import ScienceIcon from '@mui/icons-material/Science';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useNavigate } from 'react-router-dom';

import { useLabs, useStudios } from '../../queries';
import { useMobileShell } from '../../shell/useMobileShell';
import type { LabDescriptor } from '../../domain';

/**
 * Asset gallery → Lab launch affordance (Elevation pass 2026-05-26, Area 0).
 *
 * When the app boots into /assets (default route post-elevation), users need
 * a discoverable path into the labs without going through a URL guess. This
 * component surfaces the catalog of labs at the top of the gallery:
 *   - Mobile (xs/sm): horizontal chip row. Click navigates straight to the
 *     lab's default studio (first studio in the catalog). Compact for the
 *     bottom-tab IA.
 *   - Desktop (md+): card strip. Each card shows the lab name + description
 *     + an inline studio picker (small chevron menu). Click "Open" routes
 *     to `/labs/{labId}/{studioId}`.
 *
 * The studio picker on desktop defaults to the first studio; menu lets the
 * user override before opening. On mobile we drop the studio picker for
 * thumb-comfort — the lab's default studio is good enough as a starting
 * point and the user can switch inside the lab if needed.
 */
export const LabsPicker: React.FC = () => {
  const { isMobileShell } = useMobileShell();
  const { data, isLoading, error } = useLabs();

  if (isLoading) {
    return (
      <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={14} />
        <Typography variant="caption" color="text.secondary">
          Loading labs…
        </Typography>
      </Box>
    );
  }

  if (error || !data || data.labs.length === 0) {
    // Fail gracefully — gallery still functions without the picker.
    return null;
  }

  return isMobileShell ? <LabsPickerChips labs={data.labs} /> : <LabsPickerCards labs={data.labs} />;
};

/** Mobile variant — horizontal chip row, scroll-overflow with no scrollbar. */
const LabsPickerChips: React.FC<{ labs: LabDescriptor[] }> = ({ labs }) => {
  const navigate = useNavigate();

  const openLab = (labId: string) => {
    // Mobile uses the lab's default route — the LabRoute infers the default
    // studio when the path omits :studioId.
    navigate(`/labs/${labId}`);
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{
        px: 2,
        py: 1,
        overflowX: 'auto',
        whiteSpace: 'nowrap',
        borderBottom: theme => `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        // Hide the scrollbar — touch-only audience scrolls by swipe.
        '&::-webkit-scrollbar': { display: 'none' },
        scrollbarWidth: 'none',
      }}
      aria-label="Open a lab"
    >
      <Typography
        variant="overline"
        color="text.secondary"
        sx={{ alignSelf: 'center', mr: 0.5 }}
      >
        Open in
      </Typography>
      {labs.map(lab => (
        <Chip
          key={lab.id}
          label={lab.name}
          icon={labIcon(lab.canvasType)}
          variant="outlined"
          onClick={() => openLab(lab.id)}
          sx={{ flexShrink: 0 }}
        />
      ))}
    </Stack>
  );
};

/** Desktop variant — card strip with inline studio menu. */
const LabsPickerCards: React.FC<{ labs: LabDescriptor[] }> = ({ labs }) => {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{
        px: 2,
        py: 1.5,
        borderBottom: theme => `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        overflowX: 'auto',
      }}
      aria-label="Open a lab"
    >
      {labs.map(lab => (
        <LabCard key={lab.id} lab={lab} />
      ))}
    </Stack>
  );
};

const LabCard: React.FC<{ lab: LabDescriptor }> = ({ lab }) => {
  const navigate = useNavigate();
  const studios = useStudios(lab.id);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

  // Default to the first studio; the menu lets the user override before opening.
  const studioList = studios.data?.studios ?? [];
  const [selectedStudioId, setSelectedStudioId] = useState<string | null>(null);
  React.useEffect(() => {
    if (!selectedStudioId && studioList.length > 0) {
      setSelectedStudioId(studioList[0].id);
    }
  }, [studioList, selectedStudioId]);

  const selectedStudio = studioList.find(s => s.id === selectedStudioId) ?? studioList[0];

  const handleOpen = () => {
    if (selectedStudio) {
      navigate(`/labs/${lab.id}/${selectedStudio.id}`);
    } else {
      navigate(`/labs/${lab.id}`);
    }
  };

  return (
    <Card
      variant="outlined"
      sx={{
        minWidth: 220,
        maxWidth: 280,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardActionArea
        onClick={handleOpen}
        sx={{ p: 1.5, flex: 1, display: 'flex', alignItems: 'flex-start', gap: 1 }}
      >
        <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', mt: 0.25 }}>
          {labIcon(lab.canvasType)}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={600} noWrap>
            {lab.name}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {lab.description}
          </Typography>
        </Box>
      </CardActionArea>

      {studioList.length > 0 && (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{
            px: 1,
            py: 0.5,
            borderTop: theme => `1px solid ${theme.palette.divider}`,
            bgcolor: 'action.hover',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            Studio:
          </Typography>
          <Tooltip title={selectedStudio?.description ?? ''}>
            <Chip
              size="small"
              variant="outlined"
              label={selectedStudio?.name ?? 'Default'}
              deleteIcon={<ArrowDropDownIcon />}
              onClick={e => setMenuAnchor(e.currentTarget)}
              onDelete={e => setMenuAnchor(e.currentTarget as HTMLElement)}
              aria-label={`Pick a studio for ${lab.name}`}
            />
          </Tooltip>
          <Menu
            anchorEl={menuAnchor}
            open={!!menuAnchor}
            onClose={() => setMenuAnchor(null)}
          >
            {studioList.map(studio => (
              <MenuItem
                key={studio.id}
                selected={studio.id === selectedStudioId}
                onClick={() => {
                  setSelectedStudioId(studio.id);
                  setMenuAnchor(null);
                }}
              >
                {studio.name}
              </MenuItem>
            ))}
          </Menu>
        </Stack>
      )}
    </Card>
  );
};

function labIcon(canvasType: string): React.ReactElement {
  switch (canvasType.toLowerCase()) {
    case 'raster':
      return <ImageIcon fontSize="small" />;
    case 'document':
      return <DescriptionIcon fontSize="small" />;
    case 'clip':
    case 'timeline':
      return <MovieFilterIcon fontSize="small" />;
    default:
      return <ScienceIcon fontSize="small" />;
  }
}
